import React, { useState } from "react";
import { media } from "@/i18n/translations";

/**
 * Branded image holder for editorial article thumbnails.
 *
 * - If `src` is empty / missing → shows a permanent navy placeholder with the
 *   Rota logo (so legacy articles without a hero image never display random
 *   stock photos).
 * - If `src` is provided → shows the same placeholder while the image loads
 *   in the background, then cross-fades to the real photo once `onLoad` fires.
 * - Respects whatever clip-path/aspect-ratio its parent container enforces.
 */
const ArticlePreviewImage = ({ src, alt = "", imgClassName = "" }) => {
  const [loaded, setLoaded] = useState(false);
  const hasSrc = !!(src && src.trim());

  return (
    <>
      {/* Branded placeholder — always rendered underneath */}
      <div
        aria-hidden="true"
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #14294a 0%, #0B1E35 55%, #071322 100%)",
        }}
      >
        {/* subtle grid of dots echoing a nautical chart */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
        <img
          src={media.logo}
          alt=""
          className="relative w-[58%] max-w-[240px] opacity-85 drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
          draggable="false"
        />
      </div>

      {hasSrc && (
        <img
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(false)}
          className={`relative w-full h-full object-cover transition-opacity duration-700 ${
            loaded ? "opacity-100" : "opacity-0"
          } ${imgClassName}`}
          loading="lazy"
        />
      )}
    </>
  );
};

export default ArticlePreviewImage;
